/**
 * The Today brief for the garden being tended, one read shared by every page
 * that lays part of it out: Today (all of it), the Plants page's weather bar
 * and jobs, the Beds page's jobs, the Weather page's garden week and the
 * header's frost bar. The backend decides and the pages display (the
 * owner's rule; the to-do rebuild, batch 5).
 *
 * Kept under the garden, not the postcode or the day (lib/briefFreshness
 * briefKey: [TODAY_BRIEF_QUERY_KEY, gardenId, language, unit, headsUpDays]):
 * the garden's postcode and its own day are the server's, and the brief's
 * fingerprint says when either changed. In a shared garden every member
 * reads the owner's garden, so the owner's new ZIP reaches them on their
 * next read. Asked only when the server says the garden can be planned
 * (useGardenPostcode, the garden place: the owner's postcode in a shared
 * garden, never the member's own; audit A29): without that there is no
 * brief, and Today asks for the postcode instead. A garden place that could
 * not be read is this read's failure too (isError, error).
 *
 * Read again (lib/briefFreshness):
 * - when the page comes back into view (refetch on focus);
 * - after the app's own writes (gardenWritten; a to-do mark answers with the
 *   brief itself, put under queryKey; a postcode saved here reads it again);
 * - while the server says it is rebuilding the garden's outlook (the brief's
 *   `updating`), every UPDATING_POLL_MS, at most UPDATING_POLLS times; the
 *   pages show "Updating your garden..." (GuidanceUpdating);
 * - at the brief's validUntil (the garden's next midnight), by a timer, so a
 *   page left open rolls over to the new day by itself.
 * A read whose fingerprint is the one held keeps the brief held
 * (keepSameBrief), so nothing on screen redraws.
 *
 * Also here, useGardenSeason: the garden's season now as the server names it
 * (GET /today's day.season.four, the planner's season as Today's heading
 * names it), for the share caption starter (PlantCard, LocationCard,
 * ShareMoments; architecture audit A13), on the same query and key as Today
 * ([TODAY_QUERY_KEY, the garden's postcode, the garden's day], both the
 * garden place's), so it asks nothing new once Today is read.
 *
 * Where: features/today TodayPage, features/plant-list PlantGrid,
 * features/locations LocationGrid, features/weather WeatherPage,
 * shared/components/layout AlertBar, useGuidanceUpdating.
 * API: GET /today/brief?lang=&unit=&days=&device= (api/todayBrief; the device
 * for the advice about it, deviceNow).
 * Settings: the gardener's own headsUpDays, else the Grow settings'
 * today.HEADS_UP_DAYS; TODAY_QUERY_RETRIES, UPDATING_POLL_MS, UPDATING_POLLS.
 *
 * @module useTodayBrief
 */

import { retryRule } from '@/config/queryRetry';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useGardenPostcode } from './useGardenPostcode';
import { useTranslation } from './useTranslation';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { useGrowSettings } from '@/shared/lib/growSettings';
import { getTodayBrief, type TodayBrief } from '@/shared/api/todayBrief';
import { chosenGarden } from '@/shared/api/gardenMembers';
import type { TodoAsk } from '@/shared/api/todo';
import { briefKey, keepSameBrief, msUntilValid } from '@/shared/lib/briefFreshness';
import { deviceNow } from '@/features/welcome/useInstallPrompt';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { TODAY_QUERY_KEY, TODAY_QUERY_RETRIES, UPDATING_POLL_MS, UPDATING_POLLS } from '@/config/tunables/today';
import { getToday, type FourSeason } from '@/shared/api/today';

/** What a page is told besides the brief. */
export interface TodayBriefReading {
  /** The brief, or undefined until it is in (or when it cannot be asked for). */
  brief: TodayBrief | undefined;
  /** True on the first read, with nothing cached yet, and while the garden's place is on its way. */
  isLoading: boolean;
  /** True while a read is on its way, a background one with the old brief showing included. */
  isFetching: boolean;
  /**
   * True while a read is on its way because something changed (a write, the garden's midnight): the brief was
   * invalidated. A read on coming back to the page is not one: it shows nothing ("Updating your garden..." is for a
   * change), and the same fingerprint keeps the brief on screen.
   */
  rereading: boolean;
  /** The server says it is rebuilding the garden's outlook, so the brief shown may be the one before. */
  serverUpdating: boolean;
  /** True when the brief could not be had. */
  isError: boolean;
  /** The failure itself, or null. */
  error: unknown;
  /** How far ahead Coming up looks: the gardener's own choice, else the Grow settings' default; null before the app has heard it (the server then uses its own). */
  headsUpDays: number | null;
  /** No postcode, or one Grow cannot plan from: no brief is asked for. */
  noZip: boolean;
  /** The key the brief is kept under, for a to-do mark to put the brief it answers with in its place. */
  queryKey: readonly unknown[];
  /** How the brief was asked for, which a to-do mark repeats (api/todo TodoAsk). */
  ask: TodoAsk;
}

/**
 * The brief, read once for every page that shows part of it.
 *
 * @returns The brief with its state, and how it was asked for.
 */
export function useTodayBrief(): TodayBriefReading {
  const { i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const { userConfig } = useUserConfig();
  const settings = useGrowSettings();
  const queryClient = useQueryClient();
  // Whether the garden can be planned, as the server says (its owner's postcode in a shared garden).
  const place = useGardenPostcode();
  const zip = place.zip;
  const unit = userConfig.preferences.temperatureUnit;
  // The gardener's own choice, else the Grow setting's (once the app has heard it); null leaves it to the server.
  const headsUpDays = userConfig.preferences.headsUpDays ?? settings['today.HEADS_UP_DAYS'] ?? null;
  // Only once the server has said: until then Today shows neither the postcode step nor the lists.
  const noZip = place.known && !place.usable;
  /** The garden being tended: the one chosen on this device, else the person's own. */
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;

  // The device, for the advice about it (the install tips): the same for the whole visit, so not in the key.
  const device = deviceNow();
  const queryKey = briefKey(gardenId, language, unit, headsUpDays);
  const keyText = JSON.stringify(queryKey);
  const enabled = place.usable && gardenId !== null;
  /** How many times in a row the server has said it is still updating; the polling stops at UPDATING_POLLS. */
  const polls = useRef(0);
  const { data, isLoading, isFetching, isError, error }: UseQueryResult<TodayBrief> = useQuery({
    queryKey,
    queryFn: () => getTodayBrief(language, unit, headsUpDays, device),
    enabled,
    staleTime: QUERY_STALE_TIME_MS,
    retry: retryRule(TODAY_QUERY_RETRIES),
    // Coming back to the page reads again; the same fingerprint keeps what is on screen.
    refetchOnWindowFocus: 'always',
    structuralSharing: (held, next) => keepSameBrief(held as TodayBrief | undefined, next as TodayBrief),
    // While the server says it is rebuilding the outlook, ask again soon (never for ever: UPDATING_POLLS).
    refetchInterval: (query) => {
      if (query.state.data?.updating !== true) {
        polls.current = 0;
        return false;
      }
      polls.current += 1;
      return polls.current <= UPDATING_POLLS ? UPDATING_POLL_MS : false;
    },
  });
  /** The server says it is updating, and the polling has not given up. */
  const serverUpdating = data?.updating === true && polls.current <= UPDATING_POLLS;
  /** This read is for a change: the brief was invalidated (gardenWritten, the midnight timer), not merely looked at again. */
  const rereading = isFetching && (queryClient.getQueryState(queryKey)?.isInvalidated ?? false);

  // The garden's postcode changed (saved on the profile or the ZIP step): the key does not hold it, so read again now.
  const zipSeen = useRef(zip);
  useEffect(() => {
    if (zipSeen.current === zip) return;
    const before = zipSeen.current;
    zipSeen.current = zip;
    // The first postcode (or the place arriving) turns the read on, which reads the brief by itself.
    if (!before) return;
    void queryClient.invalidateQueries({ queryKey: JSON.parse(keyText) as unknown[], exact: true }, { cancelRefetch: false });
  }, [zip, keyText, queryClient]);

  // At the garden's midnight the brief is for another day: read it again then, so a page left open rolls over.
  const validUntil = data?.validUntil ?? null;
  useEffect(() => {
    if (!enabled) return undefined;
    const wait = msUntilValid(validUntil, Date.now());
    if (wait === null) return undefined;
    const timer = window.setTimeout(() => {
      // Not cancelling a read already under way: every page reading the brief sets this timer.
      void queryClient.invalidateQueries({ queryKey: JSON.parse(keyText) as unknown[], exact: true }, { cancelRefetch: false });
    }, wait);
    return () => window.clearTimeout(timer);
  }, [enabled, validUntil, keyText, queryClient]);

  return {
    brief: data, isLoading: isLoading || (gardenId !== null && !place.known && !place.isError), isFetching, rereading, serverUpdating,
    // The garden place failing is the brief's failure too: Today says the standard sentence.
    isError: isError || place.isError, error: error ?? place.error, headsUpDays, noZip, queryKey,
    ask: { lang: language, unit, days: headsUpDays, device },
  };
}

/**
 * The garden's season now, as the server names it: the planner's season for
 * this month in the garden's climate, reduced by the server to one of the
 * four. The app never works a season out from the calendar.
 *
 * @param enabled - Whether to ask the server when Today has not been read yet (the Share screen is open).
 * @returns spring, summer, fall or winter; null where the planner has no season for the place, or until it is read.
 */
export function useGardenSeason(enabled = true): FourSeason | null {
  // The garden's postcode and its own day, the server's (the owner's in a shared garden).
  const { zip, usable, date: today } = useGardenPostcode();
  const { data } = useQuery({
    queryKey: [TODAY_QUERY_KEY, zip, today],
    queryFn: () => getToday(zip),
    enabled: enabled && usable && today.length > 0,
    staleTime: QUERY_STALE_TIME_MS,
    retry: retryRule(TODAY_QUERY_RETRIES),
  });
  return data?.season?.four ?? null;
}
